import type { ErrorParams } from '@sirius/contracts/admin'
import type { i18n as I18n } from 'i18next'
import { createI18n } from './createI18n'

// The texts of what the server reports: an API error's `errorCode` is the
// catalog key `errors.<code>`, a game config issue's `code` is
// `configIssues.<code>`, and one of zod's own issues (a refused input, a
// form) is `validation.<variant>` (see `zodIssueKey`). The code's params
// fill the placeholders. A code the catalog does not know (a backend newer
// than the panel, a language that lacks it) reads as the English `message`
// the server sent, which is never run through i18next (it may hold a name
// that looks like a placeholder).
//
// This lives outside React: `toHttpError` and the zod messages run in
// providers and form schemas. The app binds its i18n instance once
// (`bindErrorTexts`); until then, and in tests, an English instance answers.

let bound: I18n | undefined

/** Makes `i18n` (the app's, switched by `PanelLanguageProvider`) answer the error texts. */
export function bindErrorTexts(i18n: I18n): void {
  bound = i18n
}

function activeI18n(): I18n {
  bound ??= createI18n()
  return bound
}

/**
 * The param whose number picks the plural form (`_one` / `_other`) of a
 * code's text, for the codes whose params do not name it `count`.
 */
const PLURAL_PARAM: Readonly<Record<string, string>> = {
  INVITATION_LIMIT_REACHED: 'limit',
  NAME_LENGTH_INVALID: 'max',
  PUBLISH_REFUSED: 'issues',
  SIMULATION_REFUSED: 'issues',
}

function translate(
  key: string,
  params: ErrorParams | undefined,
  fallback: string,
  pluralParam?: string,
): string {
  const i18n = activeI18n()
  const options: Record<string, unknown> = { ...params }
  const driver = pluralParam === undefined ? undefined : params?.[pluralParam]
  if (options.count === undefined && typeof driver === 'number') {
    options.count = driver
  }
  // with the count: a text that has only plural forms exists for a count
  if (!i18n.exists(key, options)) {
    return fallback
  }
  // `defaultValue` only guards a key that vanished between the two calls
  return i18n.t(key, { ...options, defaultValue: fallback })
}

/** The text of an API error's `errorCode`; `fallback` (the server's English message) for an unknown or missing one. */
export function errorText(
  code: string | undefined,
  params: ErrorParams | undefined,
  fallback: string,
): string {
  return code === undefined
    ? fallback
    : translate(`errors.${code}`, params, fallback, PLURAL_PARAM[code])
}

/** The text of a game config issue's `code`; `fallback` (its English message) for an unknown or missing one. */
export function configIssueText(
  code: string | undefined,
  params: ErrorParams | undefined,
  fallback: string,
): string {
  return code === undefined
    ? fallback
    : translate(`configIssues.${code}`, params, fallback)
}

/** The parts of one of zod's issues the texts name (a raw issue, or the finalized one that crossed the wire). */
export interface ZodIssueLike {
  code?: unknown
  origin?: unknown
  minimum?: unknown
  maximum?: unknown
  inclusive?: unknown
  expected?: unknown
  format?: unknown
  divisor?: unknown
  values?: unknown
}

const CAMEL = (code: string) =>
  code.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase())

function numberOf(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return value
  }
  return typeof value === 'bigint' ? Number(value) : undefined
}

/** `string` / `array` for a length limit, `undefined` for any other origin. */
function sizeKindOf(origin: string): 'string' | 'array' | undefined {
  if (origin === 'string') {
    return 'string'
  }
  return origin === 'array' || origin === 'set' ? 'array' : undefined
}

/**
 * The catalog key and params of one of zod's issues, or undefined for a
 * code with no text. Keys (under `validation.`): `invalidType.<expected>`
 * (`string`, `number`, `int`, `boolean`, `array`, `object`; param
 * `expected`), `tooSmall.<kind>` and `tooBig.<kind>` (`string` and `array`:
 * a length, plural by `count`; `number`: inclusive, `numberExclusive`;
 * params `minimum` / `maximum`, `count`), `invalidValue` (`values`),
 * `invalidFormat.<format>` (`email`, `url`, `uuid`, …; param `format`),
 * `notMultipleOf` (`divisor`), `invalidUnion`, `invalidKey`,
 * `invalidElement`, `unrecognizedKeys`, `custom`.
 */
export function zodIssueKey(
  issue: ZodIssueLike,
): { key: string; params: ErrorParams } | undefined {
  if (typeof issue.code !== 'string') {
    return undefined
  }
  const base = `validation.${CAMEL(issue.code)}`
  const params: ErrorParams = {}
  switch (issue.code) {
    case 'invalid_type':
      if (typeof issue.expected !== 'string') {
        return undefined
      }
      return {
        key: `${base}.${issue.expected}`,
        params: { expected: issue.expected },
      }
    case 'too_small':
    case 'too_big': {
      const limit = numberOf(
        issue.code === 'too_small' ? issue.minimum : issue.maximum,
      )
      const name = issue.code === 'too_small' ? 'minimum' : 'maximum'
      if (limit === undefined || typeof issue.origin !== 'string') {
        return undefined
      }
      const sizeKind = sizeKindOf(issue.origin)
      const numeric = ['number', 'int', 'bigint'].includes(issue.origin)
      if (sizeKind === undefined && !numeric) {
        return undefined
      }
      const kind =
        sizeKind ?? (issue.inclusive === false ? 'numberExclusive' : 'number')
      return {
        key: `${base}.${kind}`,
        params: { [name]: limit, count: limit },
      }
    }
    case 'invalid_value':
      params.values = Array.isArray(issue.values)
        ? issue.values
            .map((value: unknown) =>
              typeof value === 'string' ? value : JSON.stringify(value),
            )
            .join(', ')
        : ''
      return { key: base, params }
    case 'invalid_format':
      if (typeof issue.format !== 'string') {
        return undefined
      }
      return {
        key: `${base}.${issue.format}`,
        params: { format: issue.format },
      }
    case 'not_multiple_of': {
      const divisor = numberOf(issue.divisor)
      return divisor === undefined
        ? undefined
        : { key: base, params: { divisor } }
    }
    default:
      return { key: base, params }
  }
}

/** The text of one of zod's issues; `fallback` (zod's own message) when the catalog has none. */
export function zodIssueText(issue: ZodIssueLike, fallback: string): string {
  const found = zodIssueKey(issue)
  return found === undefined
    ? fallback
    : translate(found.key, found.params, fallback)
}

/** True for a code of a game config issue (`UPPER_SNAKE`); zod's are `lower_snake`. */
function isConfigIssueCode(code: string): boolean {
  return code === code.toUpperCase()
}

/**
 * The text of an issue from the API (`data.issues`): a game config issue
 * (`code` upper case), a zod issue of a refused input (`code` lower case,
 * with its limits) or a bare message.
 */
export function issueText(issue: {
  code?: unknown
  params?: unknown
  message: string
}): string {
  if (typeof issue.code !== 'string') {
    return issue.message
  }
  if (isConfigIssueCode(issue.code)) {
    return configIssueText(
      issue.code,
      isParams(issue.params) ? issue.params : undefined,
      issue.message,
    )
  }
  return zodIssueText(issue as ZodIssueLike, issue.message)
}

function isParams(value: unknown): value is ErrorParams {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (entry) => typeof entry === 'string' || typeof entry === 'number',
    )
  )
}

/** `params` of an API error's `data`, when well formed. */
export function paramsOf(value: unknown): ErrorParams | undefined {
  return isParams(value) ? value : undefined
}
